/* The single write path.
 *
 * Every mutation in the app goes through commit(). It does three things in one
 * transaction: writes the record, writes the outbox entry, and wakes the sync
 * engine. Nothing else in the codebase writes to db.
 *
 * commit() cannot fail for network reasons because it never touches the
 * network. That is the first law: the save is unconditional.
 */

import { nowIso } from '@kilroy/shared';
import type {
  Comment,
  CorrectionLog,
  Evidence,
  PriorityRanking,
  PriorityRectified,
  StatusLog,
} from '@kilroy/shared';
import { db } from './db';

export type Committable =
  | { kind: 'evidence'; record: Evidence }
  | { kind: 'correction'; record: CorrectionLog }
  | { kind: 'status'; record: StatusLog }
  | { kind: 'ranking'; record: PriorityRanking }
  | { kind: 'rectified'; record: PriorityRectified }
  | { kind: 'comment'; record: Comment }
  /** Media rows live inside their evidence; only an outbox entry is written. */
  | { kind: 'media'; record: { id: string } };

const TABLE: Record<string, keyof typeof db | ''> = {
  evidence: 'evidence',
  correction: 'corrections',
  status: 'statuses',
  ranking: 'priorityRankings',
  rectified: 'priorityRectified',
  comment: 'comments',
  media: '',
};

const listeners = new Set<() => void>();

/** Fired after every successful commit. The sync engine listens. */
export function onCommit(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

type AnyTable = { put(v: unknown): Promise<unknown> };

/**
 * Commit one or more records as a single unit.
 *
 * Passing several at once matters: saving evidence also queues each of its
 * media files, and outbox ordering is preserved, so the record always precedes
 * its media and the server never sees an orphaned object.
 */
export async function commit(...items: Committable[]): Promise<void> {
  const names = [...new Set(items.map((i) => TABLE[i.kind]).filter(Boolean))];
  const tables = [db.outbox, ...names.map((n) => db[n as 'evidence'])];

  await db.transaction('rw', tables as never, async () => {
    for (const item of items) {
      const name = TABLE[item.kind];
      if (name) await (db[name as 'evidence'] as unknown as AnyTable).put(item.record);
      await db.outbox.add({
        kind: item.kind,
        ref_id: item.record.id,
        attempts: 0,
        next_attempt_at: 0,
        created_at: nowIso(),
      });
    }
  });

  for (const fn of listeners) fn();
}

/* ── Drafts. Not evidence, so they never enter the outbox. ──────────────── */

export const saveDraft = (id: string, data: unknown) =>
  db.drafts.put({ id, data, updated_at: nowIso() });

export const loadDraft = (id: string) => db.drafts.get(id);

export const clearDraft = (id: string) => db.drafts.delete(id);
